import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  Snackbar,
  Card,
  CardContent,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  Business,
  Person,
  Subject,
  Message,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import BackToTop from '../components/BackToTop';
import SEO from '../components/SEO';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Contact form functionality will be implemented later
    setSnackbar({
      open: true,
      message: 'Contact form will be available soon. Please reach out to us via email or phone in the meantime.',
      severity: 'info',
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const contactInfo = [
    {
      icon: <Email sx={{ fontSize: 40, color: '#e3662d' }} />,
      title: 'Email Us',
      content: 'support@10DStores.in',
      description: 'Send us an email anytime',
    },
    {
      icon: <Phone sx={{ fontSize: 40, color: '#5a9b9a' }} />,
      title: 'Call Us',
      content: '+91-8977555077',
      description: 'Mon-Fri from 9am to 6pm',
    },
    {
      icon: <LocationOn sx={{ fontSize: 40, color: '#e6b8a2' }} />,
      title: 'Visit Us',
      content: 'NSIC EMDBP, ECIL, Hyderabad, Telangana, India - 500 062',
      description: 'Headquarters location',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
      <SEO
        title="Contact Us"
        description="Get in touch with 10D Stores. We are here to help you with any questions about our services or partnership opportunities."
        canonical="https://10dstores.in/contact"
      />
      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #f17a4a 0%, #f8a17a 100%)',
        py: 12,
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(241, 122, 74, 0.3)',
      }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h1" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 4, fontSize: '3.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Get in Touch
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.95, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
            Have questions about partnering with 10D? We'd love to hear from you.
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
            Fill out the form below and we'll get back to you within 24 hours.
          </Typography>
        </Container>
      </Box>

      {/* Contact Form & Info Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} lg={8}>
            <Paper elevation={8} sx={{ p: 6, borderRadius: 3, backgroundColor: 'white' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 4 }}>
                Send us a Message
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Person sx={{ mr: 1, color: '#64748B' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#2563EB',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2563EB',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Email sx={{ mr: 1, color: '#64748B' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#2563EB',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2563EB',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Business sx={{ mr: 1, color: '#64748B' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#2563EB',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2563EB',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Subject sx={{ mr: 1, color: '#64748B' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#2563EB',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2563EB',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      variant="outlined"
                      multiline
                      rows={6}
                      InputProps={{
                        startAdornment: <Message sx={{ mr: 1, color: '#64748B', alignSelf: 'flex-start', mt: 1 }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#2563EB',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2563EB',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? null : <Send />}
                      sx={{
                        backgroundColor: '#10B981',
                        color: 'white',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        px: 6,
                        py: 2,
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(16,185,129,0.4)',
                        '&:hover': {
                          backgroundColor: '#059669',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(16,185,129,0.6)'
                        },
                        '&:disabled': {
                          backgroundColor: '#94A3B8',
                          transform: 'none',
                          boxShadow: 'none',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {loading ? 'Sending Message...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {contactInfo.map((info, index) => (
                <Card key={index} elevation={4} sx={{ borderRadius: 3, backgroundColor: 'white' }}>
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 2 }}>
                      {info.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B' }}>
                      {info.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#2563EB', fontWeight: 600, mb: 1 }}>
                      {info.content}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      {info.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container >

      {/* Additional Info Section */}
      < Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 4 }}>
                Ready to Partner with 10D?
              </Typography>
              <Typography variant="h6" sx={{ color: '#5a5a5a', mb: 4, lineHeight: 1.6 }}>
                Join hundreds of businesses already thriving with 10D partnerships.
                Our team is here to help you succeed and grow your business.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  component={Link}
                  to="/business"
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#e3662d',
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    px: 6,
                    py: 2,
                    borderRadius: '12px',
                    '&:hover': {
                      backgroundColor: '#d55a1f',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 32px rgba(227, 102, 45, 0.4)'
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Learn More
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: '#5a9b9a',
                    borderWidth: 2,
                    color: '#5a9b9a',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    px: 6,
                    py: 2,
                    borderRadius: '12px',
                    '&:hover': {
                      borderColor: '#4a8a89',
                      backgroundColor: '#5a9b9a',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 32px rgba(90, 155, 154, 0.4)'
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 6,
                backgroundColor: '#F8FAFC',
                borderRadius: 3,
                textAlign: 'center',
                border: '2px dashed #CBD5E1'
              }}>
                <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#1E293B', mb: 3 }}>
                  Quick Response Time
                </Typography>
                <Typography variant="body1" sx={{ color: '#475569', mb: 4, lineHeight: 1.6 }}>
                  We understand that time is valuable. That's why we commit to responding to all inquiries within 24 hours during business days.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                      24h
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Response Time
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                      100%
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Satisfaction
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box >

      {/* Snackbar for notifications */}
      < Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar >

      <BackToTop />
    </Box >
  );
};

export default ContactPage;
